/**
 * ТЗ 0103 — общие токены графиков ERP (chart.js 4). Вынесены из
 * `pages/Marketing/charts.tsx`, чтобы графики разных страниц выглядели одной
 * системой: тот же цвет подписей, та же сетка, та же подсказка.
 *
 * Внутри `molecules/Charts` нет ни одного ключа перевода — подписи приходят
 * пропсами (тест локали маркетинга сканирует только страницу и её хуки).
 */

/*
 * ТЗ 0119: нейтральные цвета графика — переменные палитры (светлое значение
 * прежнее, тёмное — по роли); canvas их получает развёрнутыми (chartTheme).
 * Тоны серий — hex: одинаково читаются на обоих фонах.
 */

/** Цвет подписей осей и легенды. */
export const CHART_INK = 'var(--k-fg-475569)';

/** Цвет горизонтальной сетки. */
export const CHART_GRID = 'var(--k-border-eef2f7)';

/** Подложка подсказки — тёмная, как у остальных графиков ERP. */
export const CHART_TOOLTIP_BG = 'var(--k-bg-0f172a)';

/** Тоны дельт и исходов: рост, падение, внимание, нейтрально. */
export const CHART_TONES = {
  good: '#16a34a',
  bad: '#dc2626',
  warn: '#d97706',
  neutral: '#94a3b8',
  accent: '#2a78d6',
} as const;

export type ChartTone = keyof typeof CHART_TONES;

/** Подсказка chart.js в общем стиле. */
export const chartBaseTooltip = {
  backgroundColor: CHART_TOOLTIP_BG,
  titleFont: { size: 12 },
  bodyFont: { size: 12 },
  padding: 8,
};

/** Шрифт отметок осей. */
export const CHART_TICK_FONT = { size: 11 };

/** Легенда снизу, маленькие квадратики. */
export const chartLegend = {
  position: 'bottom' as const,
  labels: { boxWidth: 10, font: { size: 11 }, color: CHART_INK },
};

/**
 * Тот же цвет бледнее: сравнение (35 %), провизорный день, частичный расход.
 * `#rrggbb` + альфа; цвет темы `var(--…)` (ТЗ 0119) — через `color-mix`,
 * его canvas понимает после разворота `var()` (chartTheme); другая форма
 * цвета — как пришла.
 */
export const chartFaded = (hex: string, alpha = 0.35): string => {
  if (hex.startsWith('var(')) {
    const pct = Math.round(Math.min(Math.max(alpha, 0), 1) * 1000) / 10;
    return `color-mix(in srgb, ${hex} ${pct}%, transparent)`;
  }
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const value = Math.round(Math.min(Math.max(alpha, 0), 1) * 255);
  return `${hex}${value.toString(16).padStart(2, '0')}`;
};

/** Пунктир линии сравнения (как у ROAS «по поступившим»). */
export const CHART_COMPARE_DASH = [4, 3];

/**
 * Нейтральный сланец линии сравнения поверх стека: цвет слоя (канал, курс)
 * читался бы как ещё один слой.
 */
export const CHART_COMPARE_NEUTRAL = 'var(--k-fg-475569)';

/**
 * Отметки оси для счётчиков: только целые (без «0,5 лида»); `stepSize` —
 * шаг ровно 1 (позиции в выдаче).
 */
export const chartIntegerTicks = (
  integer: boolean,
  stepSize?: number,
): { precision?: number; stepSize?: number } => ({
  ...(integer ? { precision: 0 } : {}),
  ...(stepSize ? { stepSize } : {}),
});

/**
 * Пакет 2: без анимации перерисовки. На смене данных (шаг «день» →
 * «неделя», новый период) chart.js вёл точки из старых мест в новые, и
 * снимок или медленная машина ловили график «сжатым» в левый край при уже
 * новых подписях оси. Аналитике анимация не нужна — цифры важнее движения.
 */
export const CHART_NO_ANIMATION = false as const;

/**
 * ТЗ 0113 — палитра рядов и долей (круг/кольцо, ряды ИИ-чата). Та же, что
 * была у `CategoryDonut` финансового дашборда, — вынесена сюда, чтобы доли
 * и ряды разных страниц красились одинаково. Повтор по кругу.
 */
export const CHART_SERIES_PALETTE: ReadonlyArray<string> = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#9333ea',
  '#ef4444',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#7c3aed',
  '#dc2626',
];

/** Цвет ряда / доли по номеру. */
export const chartSeriesColor = (index: number): string =>
  CHART_SERIES_PALETTE[
    ((index % CHART_SERIES_PALETTE.length) + CHART_SERIES_PALETTE.length) %
      CHART_SERIES_PALETTE.length
  ];
